import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Icon from "@/components/Icon";
import Logo from "@/components/Logo";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Redirect after render if already authenticated
  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] sp-hero flex items-center" data-testid="page-admin-login">
      <div className="sp-container w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block"><Logo /></div>
          <h1 className="mt-6 text-3xl font-extrabold text-navy">Admin Sign In</h1>
          <p className="text-slate-600 mt-2 text-sm">Manage your SiliconPay website content & leads.</p>
        </div>

        <form onSubmit={submit} className="sp-card space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-navy"
              data-testid="admin-login-email"
              placeholder="admin@siliconpay.com.ng"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-slate-200 rounded-xl px-4 py-3 pr-10 focus:outline-none focus:border-navy"
                data-testid="admin-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Toggle password visibility"
                data-testid="admin-login-toggle-pw"
              >
                <Icon name={showPw ? "EyeOff" : "Eye"} size={18} />
              </button>
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="admin-login-error">
              {err}
            </div>
          )}

          <button type="submit" disabled={busy} className="sp-btn-primary w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="text-center text-xs text-slate-500">
            <Link to="/" className="hover:text-navy">← Back to website</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
